import { useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import {
  type AlertDefinition,
  type CreateAlertDefinitionRequest,
  type UpdateAlertDefinitionRequest,
  useCreateAlertDefinition,
  useUpdateAlertDefinition,
} from '@/hooks/useAlertDefinitions';
import { TemplatePreview, TemplateVarsReference } from '../TemplateHelp';
import { OverridesPanel } from './OverridesPanel';
import { windowProblem } from './alertWindow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const INTERVAL_PRESETS = [
  { label: '1 minute', value: 60 },
  { label: '5 minutes', value: 300 },
  { label: '15 minutes', value: 900 },
  { label: '1 hour', value: 3600 },
  { label: '6 hours', value: 21600 },
  { label: '1 day', value: 86400 },
];

const SEVERITY_OPTIONS = ['info', 'low', 'medium', 'high', 'critical'];
const CONDITION_TYPES = ['threshold', 'threshold_ratio', 'delta', 'absence'];
const WINDOW_TYPES = ['checks', 'instant', 'sustained', 'm_of_n', 'average', 'duration'];
const WINDOW_LABELS: Record<string, string> = {
  checks: 'x of the last y checks',
  instant: 'instant',
  sustained: 'sustained (time window)',
  m_of_n: 'm of n (time window)',
  average: 'average (time window)',
  duration: 'duration',
};

const AUDIT_TYPES = [
  'missing_ssh_credential',
  'missing_snmp_credential',
  'connection_health',
  'vlan_naming_consistency',
  'stp_analysis',
  'link_speed_mismatch',
];
const OPERATOR_OPTIONS = ['>', '>=', '<', '<=', '==', '!='];

interface FormState {
  name: string;
  description: string;
  taskType: string;
  severity: string;
  intervalSeconds: number;
  conditionType: string;
  conditionMetric: string;
  conditionOperator: string;
  conditionValue: string;
  conditionReferenceMetric: string;
  conditionTransform: string;
  conditionSilenceMinutes: string;
  conditionOverMinutes: string;
  conditionDeltaUnit: string;
  windowType: string;
  windowMinutes: string;
  sustainedMinDatapoints: string;
  mOfNm: string;
  mOfNn: string;
  checksX: string;
  checksY: string;
  durationConsecutiveMinutes: string;
  auditType: string;
  createIncident: boolean;
  incidentQuiet: boolean;
  incidentTitle: string;
  incidentDescription: string;
  incidentDedupe: string;
  resolveOnClear: boolean;
}

const defaultForm: FormState = {
  name: '',
  description: '',
  taskType: 'metric_alert',
  severity: 'medium',
  intervalSeconds: 300,
  conditionType: 'threshold',
  conditionMetric: '',
  conditionOperator: '>',
  conditionValue: '',
  conditionReferenceMetric: '',
  conditionTransform: '',
  conditionSilenceMinutes: '15',
  conditionOverMinutes: '5',
  conditionDeltaUnit: 'percent',
  windowType: 'checks',
  windowMinutes: '10',
  sustainedMinDatapoints: '5',
  mOfNm: '3',
  mOfNn: '5',
  checksX: '3',
  checksY: '5',
  durationConsecutiveMinutes: '10',
  auditType: 'missing_ssh_credential',
  createIncident: true,
  incidentQuiet: false,
  incidentTitle: '{device_name} alert',
  // States the rule, not the reading. A reading here would rewrite the
  // incident on every evaluation; the reading is charted on the incident
  // instead. See `templateHelp.ts` for why `{current_value}` is discouraged.
  incidentDescription: '{metric_name} crossed the {threshold} threshold on {device_name}.',
  incidentDedupe: 'task:{task_id}:device:{device_id}',
  resolveOnClear: true,
};

function numStr(val: unknown, fallback: string): string {
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  return fallback;
}

function formFromTask(task: AlertDefinition): FormState {
  const form = { ...defaultForm };
  form.name = task.name;
  form.description = task.description ?? '';
  form.taskType = task.taskType;
  form.severity = task.severity;
  form.intervalSeconds = task.intervalSeconds;

  const cfg = task.config;
  if (task.taskType === 'device_audit') {
    form.auditType = (cfg.audit_type as string) || 'missing_ssh_credential';
  } else if (task.taskType === 'metric_alert') {
    const condition = cfg.condition as Record<string, unknown> | undefined;
    const window = cfg.window as Record<string, unknown> | undefined;
    if (condition) {
      form.conditionType = (condition.type as string) || 'threshold';
      form.conditionMetric = (condition.metric as string) || '';
      form.conditionOperator = (condition.operator as string) || '>';
      form.conditionValue = numStr(condition.value, '');
      form.conditionReferenceMetric = (condition.reference_metric as string) || '';
      form.conditionTransform = (condition.transform as string) || '';
      form.conditionSilenceMinutes = numStr(condition.silence_minutes, '15');
      form.conditionOverMinutes = numStr(condition.over_minutes, '5');
      form.conditionDeltaUnit = (condition.unit as string) || 'percent';
    }
    if (window) {
      form.windowType = (window.type as string) || 'sustained';
      form.windowMinutes = numStr(window.window_minutes, '10');
      form.durationConsecutiveMinutes = numStr(window.consecutive_minutes, '10');
      const params = window.params as Record<string, unknown> | undefined;
      if (params) {
        form.sustainedMinDatapoints = numStr(params.min_datapoints, '5');
        form.mOfNm = numStr(params.m, '3');
        form.mOfNn = numStr(params.n, '5');
        form.checksX = numStr(params.x, '3');
        form.checksY = numStr(params.y, '5');
      }
    }
  }

  form.createIncident = false;
  form.resolveOnClear = false;
  // Channel actions are not read back: §4 moved delivery out of the definition and into
  // routing policy, and the migration strips them. A definition restored from an old
  // dump may still carry one; ignoring it here is deliberate, so re-saving through this
  // form cleans it up rather than round-tripping something nothing dispatches.
  for (const action of task.actions) {
    if (action.type === 'create_incident') {
      form.createIncident = true;
      form.incidentTitle = (action.title_template as string) || '';
      form.incidentDescription = (action.description_template as string) || '';
      form.incidentDedupe = (action.dedupe_key_template as string) || '';
      form.incidentQuiet = action.quiet === true;
    } else if (action.type === 'resolve_on_clear') {
      form.resolveOnClear = true;
    }
  }
  return form;
}

function buildConfigFromForm(form: FormState): Record<string, unknown> {
  if (form.taskType === 'device_audit') {
    return { audit_type: form.auditType, params: {} };
  }
  const condition: Record<string, unknown> = {
    type: form.conditionType,
    metric: form.conditionMetric,
  };
  if (form.conditionType === 'threshold') {
    condition.operator = form.conditionOperator;
    condition.value = parseFloat(form.conditionValue);
    if (form.conditionTransform) condition.transform = form.conditionTransform;
  } else if (form.conditionType === 'threshold_ratio') {
    condition.operator = form.conditionOperator;
    condition.value = parseFloat(form.conditionValue);
    condition.reference_metric = form.conditionReferenceMetric;
    if (form.conditionTransform) condition.transform = form.conditionTransform;
  } else if (form.conditionType === 'delta') {
    condition.operator = form.conditionOperator;
    condition.value = parseFloat(form.conditionValue);
    condition.unit = form.conditionDeltaUnit;
    condition.over_minutes = parseInt(form.conditionOverMinutes, 10);
    if (form.conditionTransform) condition.transform = form.conditionTransform;
  } else if (form.conditionType === 'absence') {
    condition.silence_minutes = parseInt(form.conditionSilenceMinutes, 10);
  }

  const window: Record<string, unknown> = { type: form.windowType };
  if (form.windowType === 'checks') {
    // No time window: the evaluator reads back as far as it takes to find the checks.
    window.params = { x: parseInt(form.checksX, 10), y: parseInt(form.checksY, 10) };
  } else if (form.windowType === 'duration') {
    window.consecutive_minutes = parseInt(form.durationConsecutiveMinutes, 10);
  } else {
    window.window_minutes = parseInt(form.windowMinutes, 10);
    if (form.windowType === 'sustained') {
      window.params = { min_datapoints: parseInt(form.sustainedMinDatapoints, 10) };
    } else if (form.windowType === 'm_of_n') {
      window.params = {
        m: parseInt(form.mOfNm, 10),
        n: parseInt(form.mOfNn, 10),
      };
    }
  }

  return { condition, window, filters: {} };
}

function buildActionsFromForm(form: FormState): Record<string, unknown>[] {
  const actions: Record<string, unknown>[] = [];
  if (form.createIncident) {
    actions.push({
      type: 'create_incident',
      severity: form.severity,
      title_template: form.incidentTitle,
      description_template: form.incidentDescription,
      dedupe_key_template: form.incidentDedupe,
      ...(form.incidentQuiet ? { quiet: true } : {}),
    });
  }
  if (form.resolveOnClear) actions.push({ type: 'resolve_on_clear' });
  return actions;
}

interface AlertFormModalProps {
  isOpen: boolean;
  task: AlertDefinition | null;
  onClose: () => void;
}

export function AlertFormModal({ isOpen, task, onClose }: AlertFormModalProps) {
  const [form, setForm] = useState<FormState>(defaultForm);
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time the modal opens, and if it is handed another task while
  // open. `undefined` while closed makes every open a change, even for the same task.
  const openedFor = isOpen ? task : undefined;
  const [formFor, setFormFor] = useState<AlertDefinition | null | undefined>(undefined);
  if (openedFor !== formFor) {
    setFormFor(openedFor);
    if (openedFor !== undefined) {
      setError(null);
      setForm(openedFor ? formFromTask(openedFor) : { ...defaultForm });
    }
  }

  const createMutation = useCreateAlertDefinition();
  const updateMutation = useUpdateAlertDefinition();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    const problem = form.taskType === 'metric_alert' ? windowProblem(form) : null;
    if (problem) {
      setError(problem);
      return;
    }
    const taskConfig = buildConfigFromForm(form);
    const actions = buildActionsFromForm(form);
    try {
      if (task) {
        const data: UpdateAlertDefinitionRequest = {
          name: form.name,
          description: form.description || undefined,
          severity: form.severity,
          intervalSeconds: form.intervalSeconds,
          config: taskConfig,
          actions,
        };
        await updateMutation.mutateAsync({ taskId: task.id, data });
      } else {
        const data: CreateAlertDefinitionRequest = {
          name: form.name,
          description: form.description || undefined,
          taskType: form.taskType,
          severity: form.severity,
          intervalSeconds: form.intervalSeconds,
          config: taskConfig,
          actions,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    }
  };

  return (
    <AppSheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={task ? 'Edit alert rule' : 'New alert rule'}
      size="lg"
    >
      <AppSheet.Body>
        {error ? (
          <Alert variant="destructive" className="mb-4 relative">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
            <button
              type="button"
              onClick={() => { setError(null); }}
              className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Alert>
        ) : null}

        <form
          id="alert-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="af-name">Name</Label>
            <Input
              id="af-name"
              value={form.name}
              onChange={(e) => { updateField('name', e.target.value); }}
              placeholder="e.g., High utilization alert"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="af-description">Description</Label>
            <Textarea
              id="af-description"
              rows={2}
              value={form.description}
              onChange={(e) => { updateField('description', e.target.value); }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="af-task-type">Task type</Label>
              <Select
                value={form.taskType}
                onValueChange={(v) => { updateField('taskType', v); }}
                disabled={!!task}
              >
                <SelectTrigger id="af-task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metric_alert">Metric alert</SelectItem>
                  <SelectItem value="device_audit">Device audit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="af-severity">Severity</Label>
              <Select
                value={form.severity}
                onValueChange={(v) => { updateField('severity', v); }}
              >
                <SelectTrigger id="af-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="af-interval">Interval</Label>
              <Select
                value={String(form.intervalSeconds)}
                onValueChange={(v) => { updateField('intervalSeconds', parseInt(v, 10)); }}
              >
                <SelectTrigger id="af-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={String(p.value)}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.taskType === 'metric_alert' ? (
            <>
              <Separator />
              <h3 className="text-sm font-semibold">Condition</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="af-cond-type" className="text-xs">Type</Label>
                  <Select
                    value={form.conditionType}
                    onValueChange={(v) => { updateField('conditionType', v); }}
                  >
                    <SelectTrigger id="af-cond-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="af-cond-metric" className="text-xs">Metric</Label>
                  <Input
                    id="af-cond-metric"
                    value={form.conditionMetric}
                    onChange={(e) => { updateField('conditionMetric', e.target.value); }}
                    placeholder="e.g., interface_in_octets"
                    className="font-mono"
                  />
                </div>
              </div>

              {form.conditionType !== 'absence' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="af-cond-op" className="text-xs">Operator</Label>
                    <Select
                      value={form.conditionOperator}
                      onValueChange={(v) => { updateField('conditionOperator', v); }}
                    >
                      <SelectTrigger id="af-cond-op">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATOR_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="af-cond-value" className="text-xs">Value</Label>
                    <Input
                      id="af-cond-value"
                      type="number"
                      value={form.conditionValue}
                      onChange={(e) => { updateField('conditionValue', e.target.value); }}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="af-cond-transform" className="text-xs">Transform (optional)</Label>
                    <Input
                      id="af-cond-transform"
                      value={form.conditionTransform}
                      onChange={(e) => { updateField('conditionTransform', e.target.value); }}
                      placeholder="e.g., rate_per_second"
                      className="font-mono"
                    />
                  </div>
                </div>
              ) : null}

              {form.conditionType === 'threshold_ratio' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="af-cond-ref" className="text-xs">Reference metric</Label>
                  <Input
                    id="af-cond-ref"
                    value={form.conditionReferenceMetric}
                    onChange={(e) => { updateField('conditionReferenceMetric', e.target.value); }}
                    placeholder="e.g., interface_speed"
                    className="font-mono"
                  />
                </div>
              ) : null}

              {form.conditionType === 'absence' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="af-cond-silence" className="text-xs">Silence minutes</Label>
                  <Input
                    id="af-cond-silence"
                    type="number"
                    value={form.conditionSilenceMinutes}
                    onChange={(e) => { updateField('conditionSilenceMinutes', e.target.value); }}
                    className="font-mono"
                  />
                </div>
              ) : null}

              {form.conditionType === 'delta' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="af-cond-over" className="text-xs">Over minutes</Label>
                    <Input
                      id="af-cond-over"
                      type="number"
                      value={form.conditionOverMinutes}
                      onChange={(e) => { updateField('conditionOverMinutes', e.target.value); }}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="af-cond-unit" className="text-xs">Unit</Label>
                    <Select
                      value={form.conditionDeltaUnit}
                      onValueChange={(v) => { updateField('conditionDeltaUnit', v); }}
                    >
                      <SelectTrigger id="af-cond-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percent</SelectItem>
                        <SelectItem value="absolute">Absolute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : null}

              <Separator />
              <h3 className="text-sm font-semibold">Window strategy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="af-window-type" className="text-xs">Strategy</Label>
                  <Select
                    value={form.windowType}
                    onValueChange={(v) => { updateField('windowType', v); }}
                  >
                    <SelectTrigger id="af-window-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WINDOW_TYPES.map((w) => (
                        <SelectItem key={w} value={w}>
                          {WINDOW_LABELS[w] ?? w}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.windowType !== 'duration' && form.windowType !== 'checks' ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="af-window-minutes" className="text-xs">Window (minutes)</Label>
                    <Input
                      id="af-window-minutes"
                      type="number"
                      value={form.windowMinutes}
                      onChange={(e) => { updateField('windowMinutes', e.target.value); }}
                      className="font-mono"
                    />
                  </div>
                ) : null}
              </div>

              {form.windowType === 'checks' ? (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="af-checks-x" className="text-xs">Over the threshold in (x)</Label>
                      <Input
                        id="af-checks-x"
                        type="number"
                        min={1}
                        value={form.checksX}
                        onChange={(e) => { updateField('checksX', e.target.value); }}
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="af-checks-y" className="text-xs">Of the last (y) checks</Label>
                      <Input
                        id="af-checks-y"
                        type="number"
                        min={1}
                        value={form.checksY}
                        onChange={(e) => { updateField('checksY', e.target.value); }}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A check is one poll of the device. Each port or processor is judged on its own.
                  </p>
                </div>
              ) : null}

              {(() => {
                const problem = windowProblem(form);
                return problem ? <p className="text-xs text-sev-2">{problem}</p> : null;
              })()}

              {form.windowType === 'sustained' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="af-sustained" className="text-xs">Min datapoints</Label>
                  <Input
                    id="af-sustained"
                    type="number"
                    value={form.sustainedMinDatapoints}
                    onChange={(e) => { updateField('sustainedMinDatapoints', e.target.value); }}
                    className="font-mono"
                  />
                </div>
              ) : null}

              {form.windowType === 'm_of_n' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="af-m" className="text-xs">M (violations)</Label>
                    <Input
                      id="af-m"
                      type="number"
                      value={form.mOfNm}
                      onChange={(e) => { updateField('mOfNm', e.target.value); }}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="af-n" className="text-xs">N (total points)</Label>
                    <Input
                      id="af-n"
                      type="number"
                      value={form.mOfNn}
                      onChange={(e) => { updateField('mOfNn', e.target.value); }}
                      className="font-mono"
                    />
                  </div>
                </div>
              ) : null}

              {form.windowType === 'duration' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="af-duration" className="text-xs">Consecutive minutes</Label>
                  <Input
                    id="af-duration"
                    type="number"
                    value={form.durationConsecutiveMinutes}
                    onChange={(e) => { updateField('durationConsecutiveMinutes', e.target.value); }}
                    className="font-mono"
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {form.taskType === 'device_audit' ? (
            <>
              <Separator />
              <h3 className="text-sm font-semibold">Audit configuration</h3>
              <div className="space-y-1.5">
                <Label htmlFor="af-audit-type" className="text-xs">Audit type</Label>
                <Select
                  value={form.auditType}
                  onValueChange={(v) => { updateField('auditType', v); }}
                >
                  <SelectTrigger id="af-audit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIT_TYPES.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : null}

          <Separator />
          <h3 className="text-sm font-semibold">Notification actions</h3>
          <TemplateVarsReference taskType={form.taskType} />

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.createIncident}
              onCheckedChange={(v) => { updateField('createIncident', v === true); }}
            />
            Create incident
          </label>
          {form.createIncident ? (
            <div className="ml-6 space-y-3 pb-2">
              <div className="space-y-1.5">
                <Label htmlFor="af-inc-title" className="text-xs">Title template</Label>
                <Input
                  id="af-inc-title"
                  value={form.incidentTitle}
                  onChange={(e) => { updateField('incidentTitle', e.target.value); }}
                  className="font-mono"
                />
                <TemplatePreview template={form.incidentTitle} taskType={form.taskType} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="af-inc-desc" className="text-xs">Description template</Label>
                <Input
                  id="af-inc-desc"
                  value={form.incidentDescription}
                  onChange={(e) => { updateField('incidentDescription', e.target.value); }}
                  className="font-mono"
                />
                <TemplatePreview
                  template={form.incidentDescription}
                  taskType={form.taskType}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="af-inc-dedupe" className="text-xs">Dedupe key template</Label>
                <Input
                  id="af-inc-dedupe"
                  value={form.incidentDedupe}
                  onChange={(e) => { updateField('incidentDedupe', e.target.value); }}
                  className="font-mono"
                />
                <TemplatePreview template={form.incidentDedupe} taskType={form.taskType} />
              </div>
            </div>
          ) : null}

          {form.createIncident ? (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.incidentQuiet}
                onCheckedChange={(v) => { updateField('incidentQuiet', v === true); }}
              />
              Quiet: record the incident without notifying anyone
            </label>
          ) : null}

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.resolveOnClear}
              onCheckedChange={(v) => { updateField('resolveOnClear', v === true); }}
            />
            Resolve on clear
          </label>

          {/* §4: delivery is no longer configured per definition. An incident that should
              page less is an incident whose severity is wrong — fix it here, or add an
              override — rather than a definition with its own private mailing list. */}
          <div className="rounded-md border border-border-subtle bg-muted/40 px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              Notifications follow your organization&apos;s routing policy. Who hears about this
              alert is decided by notification routes and each person&apos;s own subscriptions,
              not by the alert itself.
            </p>
          </div>

          {task ? (
            <>
              <Separator />
              <OverridesPanel taskId={task.id} />
            </>
          ) : null}
        </form>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" form="alert-form" disabled={isSaving}>
          {isSaving ? 'Saving…' : task ? 'Save changes' : 'Create'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
